import { useEffect } from 'react';
import { useAppSelector } from '@/app/hooks';
import useGtag from '@/hooks/useGtag';

const GTM_ID_PLACEHOLDER = '{GTM_ID}';
const GTM_CONTAINER_URL = 'https://www.googletagmanager.com/gtm.js';

function resolveScriptUrl(id: string, src?: string, type?: 'GTM' | 'GA4'): string {
  if (src?.trim()) {
    const pattern = new RegExp(GTM_ID_PLACEHOLDER.replace(/[{}]/g, '\\$&'), 'gi');
    return src.replace(pattern, id);
  }
  if (type === 'GTM') {
    return `${GTM_CONTAINER_URL}?id=${encodeURIComponent(id)}`;
  }
  return `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
}

declare global {
  interface Window {
    _gtmPush?: (event: any) => void;
    _gtmReady?: boolean;
    _gtmEventQueue?: any[];
    injectGtm?: (gtmId: string, resolvedScriptUrl?: string) => void;
    __GTM_ID?: string;
    __gtmInjected?: boolean;
    __injectedTrackerIds?: Set<string>;
  }
}

export const useInjectGtm = () => {
  const templateConfig = useAppSelector((state) => state.TemplateConfig);
  const { addGtmScript } = useGtag();

  useEffect(() => {
    let interval: number | undefined;

    function tryInject() {
      try {
        const trackers = templateConfig?.trackers?.gtm;
        if (!trackers || trackers.length === 0) return;

        const enabledTrackers = trackers.filter((t) => t.isEnabled && t.id);
        if (enabledTrackers.length === 0) return;

        window.__injectedTrackerIds = window.__injectedTrackerIds || new Set();

        const pending = enabledTrackers.filter((t) => !window.__injectedTrackerIds!.has(t.id));
        if (pending.length === 0) {
          clearInterval(interval);
          return;
        }

        for (const trackersEntry of pending) {
          const id = trackersEntry.id;
          const type = trackersEntry.type;
          const src = trackersEntry.src;

          window.__injectedTrackerIds.add(id);

          if (!window.__GTM_ID) {
            window.__GTM_ID = id;
          }

          if (type === 'GA4') {
            addGtmScript({ id, src, type: 'GA4' });
          } else if (typeof window.injectGtm === 'function') {
            const resolvedUrl = resolveScriptUrl(id, src, type);
            window.injectGtm(id, resolvedUrl);
          }
        }

        if (window.__injectedTrackerIds.size >= enabledTrackers.length) {
          clearInterval(interval);
        }
      } catch (e) {
        clearInterval(interval);
      }
    }

    interval = window.setInterval(tryInject, 300);
    setTimeout(() => {
      clearInterval(interval);
    }, 15000);

    tryInject();

    return () => {
      clearInterval(interval);
    };
  }, [templateConfig]);
};
